import { SUPPORT_EMAIL, SUPPORT_PHONE } from './support-contact';

/**
 * Shared reading of a failed HTTP call.
 *
 * Extracted because the same three lines were about to exist in a third place.
 * The project list, the uploader and the report export each independently decided
 * how to read a server error, and the report export decided not to read it at all.
 *
 * THE RULE THESE ENCODE: the server usually says why. Pass its words through;
 * do not paraphrase them, and never tell someone to retry a condition that cannot
 * change.
 */

/** The server's own explanation, or '' when it did not give one. */
export function serverReasonOf(error?: { error?: unknown } | null): string {
  const body = error?.error as unknown;
  if (typeof body === 'string' && body.trim()) {
    try {
      const parsed = JSON.parse(body);
      return String(parsed?.error ?? parsed?.message ?? parsed?.Message ?? '').trim();
    } catch {
      return body.trim().slice(0, 300);
    }
  }
  if (body && typeof body === 'object') {
    const b = body as { error?: string; message?: string; Message?: string };
    return String(b.error ?? b.message ?? b.Message ?? '').trim();
  }
  return '';
}

/**
 * True when retrying cannot possibly help.
 *
 * 401/403 are authorisation verdicts and 404/410 are absences: the same request
 * a second time gets the same answer. Telling a user to "try again" here is an
 * instruction to do the wrong thing repeatedly — they retry, escalate, and are
 * eventually told it is a bug somewhere else.
 *
 * status 0 is deliberately NOT permanent: no response arrived, so we do not know.
 */
export function isPermanentFailure(status?: number): boolean {
  return status === 400 || status === 401 || status === 403 || status === 404 || status === 410;
}

/** One human sentence for a failed report export, plus the server's own words. */
export function describeExportFailure(
  kind: 'PDF' | 'CSV',
  error?: { status?: number; error?: unknown } | null
): { message: string; detail: string; canRetry: boolean } {
  const status = typeof error?.status === 'number' ? error.status : 0;
  const reason = serverReasonOf(error);
  const permanent = isPermanentFailure(status);

  const head = permanent
    ? `This ${kind} cannot be generated for this project. Retrying will not change it.`
    : status === 0
    ? `Could not generate the ${kind} — no response came back. This is usually a server or ` +
      `configuration problem rather than a fault on your side, though a dropped connection ` +
      `can look the same.`
    : `Could not generate the ${kind}. Please try again.`;

  return {
    message: `${head} If you need this report, contact Vericure Support at ${SUPPORT_EMAIL} or ${SUPPORT_PHONE}.`,
    detail: [reason, status ? `HTTP ${status}` : 'no response'].filter(Boolean).join(' · '),
    canRetry: !permanent,
  };
}

/**
 * Admin-screen failure text that KEEPS THE SERVER'S REASON.
 *
 * The admin tabs used to flatten every failure to a fixed string — "Failed to
 * load drums.", "Failed to save device" — discarding an HttpErrorResponse that
 * already carried the answer. During the 2026-09-03 outage the screens said
 * "Failed to load customers." while the response body said
 * `{"error":"VericureAPI returned 500"}`; the real cause had to be recovered
 * from the browser's network tab because the UI had thrown it away.
 *
 * `action` is the human verb ("load devices", "save device"). The status is
 * included because 502-from-upstream and 403-not-entitled need different
 * responses from whoever reads the message, and a bare sentence hides that.
 */
export function describeAdminFailure(
  action: string,
  error?: { error?: unknown; status?: number; message?: string } | null,
  /**
   * Domain knowledge worth keeping, e.g. "Drums with transaction history may be
   * blocked from deletion." The point of surfacing the server's reason is to ADD
   * information, never to discard a hint someone wrote because they knew
   * something the status code does not say.
   */
  hint?: string
): string {
  const reason = serverReasonOf(error);
  const status = error?.status;
  const head = `Failed to ${action}`;

  const tail = hint ? ` ${hint}` : '';

  if (status === 0) {
    // Not a server verdict at all — the request never completed. Saying
    // "server error" here sends people to look at healthy logs.
    return `${head} — no response from the server (offline, blocked, or CORS).${tail}`;
  }
  const parts = [reason, status ? `HTTP ${status}` : ''].filter(Boolean);
  return parts.length ? `${head} — ${parts.join(' · ')}.${tail}` : `${head}.${tail}`;
}
